import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { showError } from "../../lib/feedback";
import { packagesService, ProcedimentoCatalogo } from "../../services/packages";
import { saveService } from "../../services/save";
import { ProcedureItem } from "../../types/order.types";
import { SaveResponse } from "../../types/save.types";
import { procedureFromApi, procedureToApi } from "../../lib/mappers";
import { proceduresPayload } from "./saveProcedures";
import { PATHS } from "../../app/paths";

// "10B" → [10, "B"]. Porte maior primeiro; sem porte, mantém a ordem.
const parsePorte = (porte?: string | null) => {
  const m = porte?.match(/(\d+)([A-Z])/);
  return m ? { n: Number(m[1]), l: m[2] } : null;
};
const byPorte = (a?: string | null, b?: string | null) => {
  const pa = parsePorte(a), pb = parsePorte(b);
  if (!pa || !pb) return 0;
  return pa.n !== pb.n ? pb.n - pa.n : pb.l.localeCompare(pa.l);
};

// Escolha Livre: os códigos sugeridos pela IA (checkboxItems) + o catálogo da especialidade.
export const useFreeChoice = () => {
  const { checkboxItems, setCheckboxItems, initialCheckboxItems, setCheckedItems, setModificadoProcedimentos, timestamp } = useOrderDraft();
  const { metadata } = useSession();
  const { request, loading, apiFetch } = useApi<SaveResponse>();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<ProcedimentoCatalogo[] | null>(null);

  useEffect(() => {
    packagesService
      .procedimentos(apiFetch, metadata.especialidade)
      .then(setCatalog)
      .catch((err) => {
        setCatalog([]);
        showError(err);
      });
  }, [apiFetch, metadata.especialidade]);

  const selected = useMemo(() => {
    const porte = new Map(catalog?.map((p) => [p.cod_procedimento, p.porte]));
    return checkboxItems
      .filter((row) => row[0]) // pedidos salvos trazem [] nas linhas que não casaram
      .map(([code, description, quantity]) => procedureFromApi({ code, description, quantity }))
      .sort((a, b) => byPorte(porte.get(a.code), porte.get(b.code)));
  }, [checkboxItems, catalog]);

  const update = (items: ProcedureItem[]) => {
    setCheckboxItems(items.map(procedureToApi).map((r) => [r.code, r.description, r.quantity]));
    setModificadoProcedimentos(true);
  };

  const confirm = async () => {
    const rows = selected.map(procedureToApi);
    setCheckedItems(rows);
    const payload = proceduresPayload(timestamp, initialCheckboxItems, rows, { tipo: "escolha-livre", tipoDoPacote: "", indice: -1 });
    const result = await request(() => saveService.post(apiFetch, payload));
    if (result?.status === "success") navigate(PATHS.opme);
  };

  return {
    catalog,
    selected,
    add: (code: string, description: string) => update([...selected, { code, description, quantity: 1 }]),
    remove: (code: string) => update(selected.filter((p) => p.code !== code)),
    setQuantity: (code: string, quantity: number) => update(selected.map((p) => (p.code === code ? { ...p, quantity } : p))),
    confirm,
    saving: loading,
  };
};
